<template>
    <div class="relative mt-10">
        <div class="absolute inset-0 z-10">
            <nuxt-img 
                :src="bgImageSrc" 
                :alt="bgImageAlt" 
                width="1440" 
                height="842"
                format="webp"
                loading="lazy"
                sizes="xs:380px sm:480px md:760px lg:1024px xl:1920px 2xl:1920px"
                class="object-cover object-left w-full h-full xl:object-top"
            />
        </div>

        <div class="relative mx-auto max-w-screen-2xl">
            <div class="relative z-50 px-4 pb-10 mx-auto mt-10 max-w-3xl md:mt-20 lg:mt-0 md:pb-20 2xl:mt-0 2xl:px-0">
                <h2 class="text-2xl text-center text-black xl:leading-tight font-ww-bold xl:text-42 md:text-3xl xl:px-8" v-html="mainTitle"></h2>

                <p class="mt-5 text-base text-left text-black md:mt-10 xl:text-xl md:text-lg font-ww-regular" v-html="introText"></p>

                <div class="mt-10 space-y-6 md:ml-10 xl:mt-20">
                    <div 
                        v-for="(item, index) in studyItems" 
                        :key="index"
                        class="text-base text-left text-black xl:text-xl md:text-lg font-ww-regular"
                        v-html="(item as any).text"
                    ></div>
                </div>

                <p class="mt-4 text-base text-left text-black xl:text-xl md:text-lg font-ww-bold" v-html="closingText"></p>
            </div>

            <nuxt-img 
                :src="productImageSrc" 
                :alt="productImageAlt" 
                :width="productImageWidth"
                :height="productImageHeight"
                class="relative z-10 px-4 mx-auto mb-10 xl:-bottom-40 xl:right-0 xl:absolute xl:mx-0 lg:px-0 xl:mb-0"
                format="webp"
                loading="lazy"
                sizes="xs:336px sm:393px md:520px lg:520px xl:552px 2xl:552px"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps({
  bgImageSrc: {
    type: String,
    default: '/assets/images/background-sec-2.png'
  },
  bgImageAlt: {
    type: String,
    default: 'Background Image'
  },
  mainTitle: {
    type: String,
    default: 'Backed by Science. Proven in the Real World.'
  },
  introText: {
    type: String,
    default: 'Each ingredient in Mindflow has been studied for its role in supporting focus, memory, energy, or resilience. Here\'s just a glimpse:'
  },
  studyItems: {
    type: Array,
    default: () => [
      {
        text: '<strong>Caffeine + Cognitive Performance</strong> – A 2020 study in Frontiers in Psychology found that low doses of caffeine improve executive function, flexibility, and task switching by boosting prefrontal cortex activity'
      },
      {
        text: '<strong>Alpha GPC + Focus & Memory</strong> – Clinical trials show Alpha GPC enhances memory and learning by supporting acetylcholine production (McDaniel et al., Clinical Therapeutics, 2003).'
      },
      {
        text: '<strong>Cognitiq® + BDNF</strong> – Research shows Cognitiq® increases brain-derived neurotrophic factor (BDNF), an essential protein for learning and neuroplasticity (FutureCeuticals Clinical Report, 2015).'
      },
      {
        text: '<strong>Lion\'s Mane + Neurogenesis</strong> – Lion\'s Mane supports Nerve Growth Factor (NGF), which helps with healthy brain cell development (Mori et al., Biomedical Research, 2009).'
      },
      {
        text: '<strong>Chaga + Antioxidants</strong> – Studies highlight Chaga\'s antioxidant and anti-inflammatory properties, supporting a clear, focused mind (Park et al., Journal of Ethnopharmacology, 2005).'
      }
    ]
  },
  closingText: {
    type: String,
    default: 'Mindflow doesn\'t just feel good—it\'s built on solid science.'
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-right-1.png'
  },
  productImageAlt: {
    type: String,
    default: 'Mindflow Product'
  },
  productImageWidth: {
    type: Number,
    default: 560
  },
  productImageHeight: {
    type: Number,
    default: 651
  }
})

defineBuilderComponent({
  friendlyName: 'Background Image Info With Product',
  inputs: [
    {
      name: 'bgImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/background-sec-2.png'
    },
    {
      name: 'bgImageAlt',
      type: 'text',
      defaultValue: 'Background Image'
    },
    {
      name: 'mainTitle',
      type: 'richText',
      defaultValue: 'Backed by Science. Proven in the Real World.'
    },
    {
      name: 'introText',
      type: 'richText',
      defaultValue: 'Each ingredient in Mindflow has been studied for its role in supporting focus, memory, energy, or resilience. Here\'s just a glimpse:'
    },
    {
      name: 'studyItems',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'richText',
          defaultValue: ''
        }
      ],
      defaultValue: [
        {
          text: '<strong>Caffeine + Cognitive Performance</strong> – A 2020 study in Frontiers in Psychology found that low doses of caffeine improve executive function, flexibility, and task switching by boosting prefrontal cortex activity'
        },
        {
          text: '<strong>Alpha GPC + Focus & Memory</strong> – Clinical trials show Alpha GPC enhances memory and learning by supporting acetylcholine production (McDaniel et al., Clinical Therapeutics, 2003).'
        },
        {
          text: '<strong>Cognitiq® + BDNF</strong> – Research shows Cognitiq® increases brain-derived neurotrophic factor (BDNF), an essential protein for learning and neuroplasticity (FutureCeuticals Clinical Report, 2015).'
        },
        {
          text: '<strong>Lion\'s Mane + Neurogenesis</strong> – Lion\'s Mane supports Nerve Growth Factor (NGF), which helps with healthy brain cell development (Mori et al., Biomedical Research, 2009).'
        },
        {
          text: '<strong>Chaga + Antioxidants</strong> – Studies highlight Chaga\'s antioxidant and anti-inflammatory properties, supporting a clear, focused mind (Park et al., Journal of Ethnopharmacology, 2005).'
        }
      ]
    },
    {
      name: 'closingText',
      type: 'richText',
      defaultValue: 'Mindflow doesn\'t just feel good—it\'s built on solid science.'
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/mindflow-right-1.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'Mindflow Product'
    },
    {
      name: 'productImageWidth',
      type: 'number',
      defaultValue: 552
    },
    {
      name: 'productImageHeight',
      type: 'number',
      defaultValue: 638
    }
  ]
})
</script>
